import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Lock, Eye, Loader2, Layers, TrendingUp, TrendingDown,
  Minus, HelpCircle, Star, Crown, Sparkles, ShieldAlert, Check, Target as TargetIcon,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import ChartEngine from "@/components/ChartEngine";
import { UnlockList } from "@/components/mission/UnlockCard";
import { BossRetry, StageUnlocked } from "@/components/mission/Boss";

const TF_ORDER = ["4h", "1h", "15m", "5m"];
const IND = [
  { key: "vwap", label: "VWAP" }, { key: "ema9", label: "EMA 9" }, { key: "ema20", label: "EMA 20" },
  { key: "ema50", label: "EMA 50" }, { key: "ema200", label: "EMA 200" }, { key: "rsi", label: "RSI" },
  { key: "volume", label: "Volume" },
];
const CONFLUENCE = [
  "MARKET_STRUCTURE", "HIGHER_TIMEFRAME", "SUPPORT_RESISTANCE", "VWAP",
  "MOVING_AVERAGES", "VOLUME", "MOMENTUM", "RSI", "DIVERGENCE", "RISK_LOCATION",
];
const STATES = [
  { key: "SUPPORTS_LONG", label: "Long", cls: "bg-tc-bull/15 text-tc-bull border-tc-bull/40" },
  { key: "SUPPORTS_SHORT", label: "Short", cls: "bg-tc-bear/15 text-tc-bear border-tc-bear/40" },
  { key: "NEUTRAL", label: "Neutral", cls: "bg-white/10 text-tc-sub border-white/20" },
  { key: "CAUTION", label: "Caution", cls: "bg-tc-xp/15 text-tc-xp border-tc-xp/40" },
  { key: "NOT_USED", label: "—", cls: "bg-transparent text-tc-muted border-white/10" },
];
const BIAS_OPTS = ["bullish", "bearish", "range", "unclear"];
const STRUCT_OPTS = ["bullish", "bearish", "range", "transition", "unclear"];
const MOM_OPTS = ["strengthening", "weakening", "neutral", "unclear"];
const DIR_OPTS = ["long", "short", "neutral"];

function log(type, data) { api.post("/event", { type, data: data || {} }).catch(() => {}); }

function Seg({ label, value, options, onChange, testid }) {
  return (
    <div>
      <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">{label}</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => (
          <button key={o} data-testid={`${testid}-${o}`} onClick={() => onChange(o)}
            className={`px-2.5 py-1 rounded-md text-xs capitalize border transition-colors ${value === o ? "bg-tc-coach/20 text-tc-coach border-tc-coach/50" : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"}`}>
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Analyze() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const scenario = params.get("scenario");
  const missionId = params.get("mission");

  const [phase, setPhase] = useState("loading");
  const [state, setState] = useState(null);        // replay state
  const [tf, setTf] = useState("5m");
  const [viewed, setViewed] = useState(["5m"]);
  const [toggles, setToggles] = useState({ volume: true, vwap: false, ema9: false, ema20: false, ema50: false, ema200: false, rsi: false });

  // analysis panel
  const [htf, setHtf] = useState("");
  const [structure, setStructure] = useState("");
  const [momentum, setMomentum] = useState("");
  const [bias, setBias] = useState("");
  const [levels, setLevels] = useState([]);
  const [levelInput, setLevelInput] = useState("");

  // confluence
  const [factors, setFactors] = useState({});      // category -> state

  // decision + plan
  const [decision, setDecision] = useState("");
  const [entry, setEntry] = useState("");
  const [stop, setStop] = useState("");
  const [target, setTarget] = useState("");
  const [thesis, setThesis] = useState("");
  const [invalidation, setInvalidation] = useState("");
  const [stopIsInval, setStopIsInval] = useState(true);
  const [confidence, setConfidence] = useState(0);

  const [busy, setBusy] = useState(false);
  const [snapId, setSnapId] = useState(null);
  const [review, setReview] = useState(null);
  const [hind, setHind] = useState({});
  const [summary, setSummary] = useState(null);

  const start = useCallback(async () => {
    try {
      let sid = scenario;
      if (!sid) {
        const a = await api.get("/training/adaptive-next?stage=3");
        sid = a.data.scenario_id;
      }
      const { data } = await api.post("/replay/start", { session_id: sid, mission_id: missionId });
      setState(data);
      if (data.entry == null && data.candles?.length) {
        const last = data.candles[data.candles.length - 1].close;
        setEntry(last); setStop(+(last * 0.99).toFixed(2)); setTarget(+(last * 1.02).toFixed(2));
        setLevelInput(String(last));
      }
      setPhase("analyze");
      log("analysis_started", { scenario });
      if (missionId === "boss3") log("boss3_started", { scenario });
    } catch (e) { toast.error(apiErr(e)); }
  }, [scenario, missionId]);

  useEffect(() => { start(); }, [start]);

  const switchTf = (t) => { setTf(t); setViewed((v) => v.includes(t) ? v : [...v, t]); log("timeframe_switched", { tf: t }); };
  const toggleInd = (k) => {
    setToggles((p) => { const n = { ...p, [k]: !p[k] }; log(n[k] ? "indicator_enabled" : "indicator_disabled", { indicator: k }); return n; });
  };
  const setFactor = (cat, st) => {
    setFactors((p) => ({ ...p, [cat]: st }));
    if (st !== "NOT_USED") log("confluence_factor_added", { category: cat, state: st });
  };

  const candlesForTf = () => {
    if (tf === "5m" || !state?.mtf) return state?.candles || [];
    return state.mtf[tf] || [];
  };
  const forming = () => { const arr = candlesForTf(); return arr.length && arr[arr.length - 1].forming; };

  const rr = (() => {
    const e = +entry, s = +stop, t = +target;
    if (!e || !s || !t) return null;
    const risk = decision === "SHORT" ? s - e : e - s;
    const reward = decision === "SHORT" ? e - t : t - e;
    if (risk <= 0 || reward <= 0) return null;
    return +(reward / risk).toFixed(2);
  })();

  const conf = (() => {
    let sl = 0, ss = 0, ca = 0, ne = 0, un = 0;
    CONFLUENCE.forEach((c) => {
      const st = factors[c] || "NOT_USED";
      if (st === "SUPPORTS_LONG") sl++; else if (st === "SUPPORTS_SHORT") ss++;
      else if (st === "CAUTION") ca++; else if (st === "NEUTRAL") ne++; else un++;
    });
    const supporting = decision === "LONG" ? sl : decision === "SHORT" ? ss : Math.max(sl, ss);
    const conflicting = decision === "LONG" ? ss + ca : decision === "SHORT" ? sl + ca : ca + Math.min(sl, ss);
    return { supporting, conflicting, unassessed: un, sl, ss, ca, ne };
  })();

  const addLevel = () => {
    const v = +levelInput;
    if (v > 0 && !levels.includes(v)) setLevels((l) => [...l, v].sort((a, b) => a - b));
  };

  const canLock = decision && (decision === "PASS" || (entry && stop && target && rr)) && confidence >= 1;

  const doLock = async () => {
    if (!canLock) { toast.error("Choose a decision, a plan (if trading) and record your confidence."); return; }
    setBusy(true);
    try {
      const payload = {
        attempt_id: state.attempt_id, mission_id: missionId, active_timeframe: tf,
        mtf_viewed: viewed, htf_bias: htf, structure, key_levels: levels, momentum, bias,
        decision, confidence, thesis, invalidation, stop_is_invalidation: stopIsInval,
        confluence: CONFLUENCE.map((c) => ({ category: c, state: factors[c] || "NOT_USED" })),
      };
      if (decision !== "PASS") { payload.entry = +entry; payload.stop = +stop; payload.target = +target; }
      const { data } = await api.post("/analysis/lock", payload);
      setSnapId(data.snapshot_id);
      // create the trade so history reveal can show the outcome
      if (decision !== "PASS") {
        try {
          await api.post("/trade/lock", {
            attempt_id: state.attempt_id, direction: decision, entry: +entry, stop: +stop, target: +target,
            thesis, confidence, confluence: payload.confluence, checklist: { checked_higher_timeframe: viewed.includes("4h") },
          });
        } catch (e) { /* PASS-only or invalid plan handled by score */ }
      }
      log("confluence_completed", { supporting: conf.supporting, conflicting: conf.conflicting });
      // reveal the full history
      let s = state;
      for (let i = 0; i < 60; i++) {
        const r = await api.post("/replay/advance", { attempt_id: state.attempt_id, count: 20 });
        s = r.data; if (s.at_end) break;
      }
      const rev = await api.get(`/analysis/${data.snapshot_id}/review`);
      setReview(rev.data); setPhase("review");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const complete = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/training/analyst-complete", { snapshot_id: snapId, mission_id: missionId, hindsight: hind });
      setSummary(data); setPhase("summary");
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  if (phase === "loading") return <div className="p-8 text-tc-muted font-mono text-sm">Loading classified market…</div>;
  if (phase === "summary" && summary) return <AnalystSummary s={summary} navigate={navigate} />;
  if (phase === "review" && review) return <Review review={review} hind={hind} setHind={setHind} complete={complete} busy={busy} isBoss={missionId === "boss3"} />;

  const cands = candlesForTf();

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto" data-testid="analyze-workspace">
      <button onClick={() => navigate("/training")} className="text-sm text-tc-muted hover:text-tc-txt flex items-center gap-1.5 mb-4"><ArrowLeft className="w-4 h-4" /> Trader Path</button>

      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono px-2 py-1 rounded bg-tc-bear/15 text-tc-bear tracking-widest">REAL HISTORICAL MARKET — CLASSIFIED</span>
          {missionId === "boss3" && <span className="text-[10px] font-mono px-2 py-1 rounded bg-tc-xp/15 text-tc-xp tracking-widest">BOSS #3 · THE ANALYST</span>}
        </div>
        <span className="text-[11px] font-mono text-tc-muted">Top-down analysis · future hidden · scored on process</span>
      </div>

      {/* timeframe switcher */}
      <div className="flex items-center gap-2 mb-2" data-testid="analyze-tf-switcher">
        <Layers className="w-4 h-4 text-tc-coach" />
        {TF_ORDER.map((t) => (
          <button key={t} data-testid={`analyze-tf-${t}`} onClick={() => switchTf(t)}
            className={`px-3.5 py-1.5 rounded-lg text-xs font-mono transition-colors ${tf === t ? "bg-tc-coach text-tc-bg" : "bg-white/5 text-tc-sub hover:text-tc-txt"} ${viewed.includes(t) ? "" : "ring-1 ring-tc-coach/40"}`}>
            {t.toUpperCase()}
          </button>
        ))}
        {forming() && <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-tc-xp/15 text-tc-xp ml-1">LAST {tf.toUpperCase()} CANDLE FORMING</span>}
      </div>

      {/* indicator toggles */}
      <div className="flex flex-wrap gap-1.5 mb-3">
        {IND.map((i) => (
          <button key={i.key} data-testid={`indicator-toggle-${i.key}`} onClick={() => toggleInd(i.key)}
            className={`px-2.5 py-1 rounded-md text-[11px] font-mono border transition-colors ${toggles[i.key] ? "bg-tc-coach/20 text-tc-coach border-tc-coach/50" : "bg-white/5 text-tc-muted border-white/10 hover:border-white/30"}`}>
            {i.label}
          </button>
        ))}
      </div>

      <ChartEngine candles={cands} toggles={toggles} symbol={state.session.symbol} interval={tf}
        overlays={decision !== "PASS" && decision ? { entry: +entry || null, stop: +stop || null, target: +target || null } : null}
        lines={levels.map((p) => ({ price: p, color: "#06B6D4", title: "LEVEL" }))} height={400} hideHeader />

      <div className="grid lg:grid-cols-2 gap-4 mt-5">
        {/* ANALYSIS PANEL */}
        <div className="tc-card p-5 space-y-4" data-testid="analysis-panel">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Analysis</div>
          <Seg label="Higher-Timeframe Bias" value={htf} options={BIAS_OPTS} onChange={setHtf} testid="htf-bias" />
          <Seg label="Market Structure" value={structure} options={STRUCT_OPTS} onChange={setStructure} testid="structure" />
          <Seg label="Momentum" value={momentum} options={MOM_OPTS} onChange={setMomentum} testid="momentum" />
          <Seg label="Bias" value={bias} options={DIR_OPTS} onChange={setBias} testid="bias" />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">Key Levels</div>
            <div className="flex gap-2">
              <input data-testid="level-input" type="number" value={levelInput} onChange={(e) => setLevelInput(e.target.value)}
                className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-1.5 text-sm font-mono" placeholder="price" />
              <button data-testid="add-level-btn" onClick={addLevel} className="px-3 py-1.5 rounded-md bg-white/10 text-sm hover:bg-white/20">Mark</button>
            </div>
            <div className="flex flex-wrap gap-1.5 mt-2">
              {levels.map((l) => (
                <span key={l} className="text-[11px] font-mono px-2 py-0.5 rounded bg-tc-coach/10 text-tc-coach flex items-center gap-1">
                  ${l.toLocaleString()}
                  <button onClick={() => setLevels((x) => x.filter((y) => y !== l))} className="text-tc-muted hover:text-tc-bear">×</button>
                </span>
              ))}
              {!levels.length && <span className="text-[11px] text-tc-muted">Mark the support/resistance zones you see.</span>}
            </div>
          </div>
        </div>

        {/* CONFLUENCE BUILDER */}
        <div className="tc-card p-5" data-testid="confluence-builder">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach">Confluence Builder</div>
          <p className="text-[11px] text-tc-muted mt-1">Classify each piece of evidence. Correlated factors are not independent confirmations.</p>
          <div className="mt-3 space-y-2">
            {CONFLUENCE.map((c) => (
              <div key={c} className="flex items-center justify-between gap-2">
                <span className="text-[12px] text-tc-sub flex-1">{c.replace(/_/g, " ")}</span>
                <div className="flex gap-1" data-testid={`confluence-${c}`}>
                  {STATES.map((s) => (
                    <button key={s.key} data-testid={`confluence-${c}-${s.key}`} onClick={() => setFactor(c, s.key)}
                      title={s.label}
                      className={`px-2 py-0.5 rounded text-[10px] border transition-colors ${(factors[c] || "NOT_USED") === s.key ? s.cls : "bg-transparent text-tc-muted border-white/10 hover:border-white/30"}`}>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-tc-bull/10 py-2"><div className="font-mono text-lg text-tc-bull" data-testid="conf-supporting">{conf.supporting}</div><div className="text-[9px] text-tc-muted uppercase">Supporting</div></div>
            <div className="rounded-lg bg-tc-xp/10 py-2"><div className="font-mono text-lg text-tc-xp" data-testid="conf-conflicting">{conf.conflicting}</div><div className="text-[9px] text-tc-muted uppercase">Conflict / Caution</div></div>
            <div className="rounded-lg bg-white/5 py-2"><div className="font-mono text-lg text-tc-sub" data-testid="conf-unassessed">{conf.unassessed}</div><div className="text-[9px] text-tc-muted uppercase">Unassessed</div></div>
          </div>
          <div className="text-center text-[12px] font-mono text-tc-txt mt-3 tracking-wide">DOES THE EVIDENCE JUSTIFY THE RISK?</div>
        </div>
      </div>

      {/* DECISION + PLAN */}
      <div className="tc-card p-5 mt-4" data-testid="decision-panel">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-3">Your Decision</div>
        <div className="grid grid-cols-3 gap-2">
          {[["LONG", "bull"], ["SHORT", "bear"], ["PASS", "pass"]].map(([d]) => (
            <button key={d} data-testid={`decision-${d.toLowerCase()}`} onClick={() => setDecision(d)}
              className={`py-3 rounded-lg font-heading font-bold border transition-colors ${decision === d
                ? d === "LONG" ? "bg-tc-bull/20 text-tc-bull border-tc-bull/50" : d === "SHORT" ? "bg-tc-bear/20 text-tc-bear border-tc-bear/50" : "bg-tc-pass/20 text-tc-pass border-tc-pass/50"
                : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"}`}>{d}</button>
          ))}
        </div>

        {decision && decision !== "PASS" && (
          <div className="grid sm:grid-cols-3 gap-3 mt-4">
            {[["Entry", entry, setEntry, "entry"], ["Stop (invalidation)", stop, setStop, "stop"], ["Target", target, setTarget, "target"]].map(([lab, val, set, tid]) => (
              <div key={tid}>
                <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1">{lab}</div>
                <input data-testid={`plan-${tid}`} type="number" value={val} onChange={(e) => set(e.target.value)}
                  className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
            ))}
          </div>
        )}
        {decision && decision !== "PASS" && (
          <div className="mt-2 text-[12px] font-mono flex items-center gap-3">
            <span className={rr ? (rr >= 2 ? "text-tc-bull" : rr >= 1.5 ? "text-tc-xp" : "text-tc-bear") : "text-tc-muted"} data-testid="plan-rr">R:R {rr ? `${rr}:1` : "—"}</span>
            <label className="flex items-center gap-1.5 text-tc-sub"><input type="checkbox" checked={stopIsInval} onChange={(e) => setStopIsInval(e.target.checked)} /> My stop is my invalidation</label>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1">My Thesis</div>
            <textarea data-testid="thesis-input" value={thesis} onChange={(e) => setThesis(e.target.value)} rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="Why is this trade (or PASS) worth it?" />
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1">My Idea Is Wrong If…</div>
            <textarea data-testid="invalidation-input" value={invalidation} onChange={(e) => setInvalidation(e.target.value)} rows={2}
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" placeholder="Define where your idea is objectively wrong." />
          </div>
        </div>

        {/* confidence */}
        <div className="mt-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-tc-muted mb-1.5">How Confident Are You? (1–5)</div>
          <div className="flex gap-2" data-testid="confidence-row">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} data-testid={`confidence-${n}`} onClick={() => { setConfidence(n); log("confidence_recorded", { confidence: n }); }}
                className={`w-10 h-10 rounded-lg font-mono border transition-colors ${confidence === n ? "bg-tc-coach text-tc-bg border-tc-coach" : "bg-white/5 text-tc-sub border-white/10 hover:border-white/30"}`}>{n}</button>
            ))}
          </div>
          <p className="text-[11px] text-tc-muted mt-1.5">Confidence is tracked against your reasoning quality — not the outcome.</p>
        </div>

        <button data-testid="lock-analysis-btn" onClick={doLock} disabled={busy || !canLock}
          className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-tc-xp text-tc-bg font-bold py-3.5 hover:bg-tc-xp/90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} LOCK ANALYSIS & REVEAL HISTORY
        </button>
        {!canLock && <p className="text-center text-[11px] text-tc-muted mt-2">Pick a decision{decision && decision !== "PASS" ? ", set entry/stop/target" : ""} and record your confidence to lock.</p>}
      </div>
    </div>
  );
}

/* ------------------------------- REVIEW ------------------------------- */
function Review({ review, hind, setHind, complete, busy, isBoss }) {
  const snap = review.snapshot;
  const [tfThen, setTfThen] = useState("5m");
  const thenC = tfThen === "5m" ? review.then.candles : (review.then.mtf?.[tfThen] || review.then.candles);
  const nextC = review.next.candles;
  const answered = review.hindsight_prompts.filter((p) => (hind[p.key] || "").trim()).length;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-5" data-testid="decision-review">
      <div className="text-center">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Decision Review</div>
        <h1 className="font-heading text-2xl font-bold mt-1">Outcome ≠ Decision Quality</h1>
        <p className="text-tc-sub text-sm mt-1">Compare what you saw then with what happened next — without rewriting your original analysis.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="tc-card p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-2">What You Saw Then</div>
          <div className="flex gap-1.5 mb-2">
            {TF_ORDER.filter((t) => t === "5m" || review.then.mtf?.[t]).map((t) => (
              <button key={t} onClick={() => setTfThen(t)} className={`px-2.5 py-1 rounded text-[11px] font-mono ${tfThen === t ? "bg-tc-coach text-tc-bg" : "bg-white/5 text-tc-sub"}`}>{t.toUpperCase()}</button>
            ))}
          </div>
          <ChartEngine candles={thenC} toggles={{ volume: true }} symbol={review.reveal.symbol} interval={tfThen} height={260} hideHeader />
          <div className="mt-3 text-[12px] space-y-1">
            <div><span className="text-tc-muted">Bias:</span> <span className="capitalize">{snap.bias || "—"}</span> · <span className="text-tc-muted">Decision:</span> <span className="font-semibold">{snap.decision}</span></div>
            <div className="text-tc-sub">"{snap.thesis || "No thesis recorded."}"</div>
            <div className="text-tc-muted">Invalidation: {snap.invalidation || "—"} · Confidence {snap.confidence}/5</div>
          </div>
        </div>

        <div className="tc-card p-4">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-xp mb-2">What Happened Next</div>
          <ChartEngine candles={nextC} toggles={{ volume: true }} symbol={review.reveal.symbol} interval="5m" height={260} hideHeader
            overlays={snap.decision !== "PASS" ? { entry: snap.entry, stop: snap.stop, target: snap.target } : null} />
          {review.trade ? (
            <div className="mt-3 text-[12px]">
              <span className="text-tc-muted">Outcome:</span> {review.trade.exit_reason || "open"} · P&L <span className={review.trade.pnl >= 0 ? "text-tc-bull" : "text-tc-bear"}>${review.trade.pnl}</span> · {review.trade.r_multiple}R
            </div>
          ) : <div className="mt-3 text-[12px] text-tc-pass">You PASSED — no capital was risked.</div>}
          <div className="mt-2 text-[11px] font-mono text-tc-muted">{review.reveal.symbol} · {review.reveal.category?.replace(/_/g, " ")} · {review.reveal.is_real ? review.reveal.source : "SIMULATED"}</div>
        </div>
      </div>

      <div className="tc-card p-5">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach mb-3">Hindsight Training</div>
        <div className="space-y-3">
          {review.hindsight_prompts.map((p) => (
            <div key={p.key}>
              <div className="text-[13px] text-tc-sub mb-1">{p.q}</div>
              <input data-testid={`hindsight-${p.key}`} value={hind[p.key] || ""} onChange={(e) => setHind((h) => ({ ...h, [p.key]: e.target.value }))}
                className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm" />
            </div>
          ))}
        </div>
        <button data-testid="complete-analysis-btn" onClick={complete} disabled={busy}
          className="mt-5 w-full flex items-center justify-center gap-2 rounded-lg bg-tc-coach text-tc-bg font-bold py-3.5 hover:bg-tc-coach/90 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} See My Process Score <ArrowRight className="w-4 h-4" />
        </button>
        <p className="text-center text-[11px] text-tc-muted mt-2">{answered}/{review.hindsight_prompts.length} reflections answered (optional but valuable).</p>
      </div>
    </div>
  );
}

/* ------------------------------- SUMMARY ------------------------------- */
function AnalystSummary({ s, navigate }) {
  const labels = {
    market_context: "Market Context", structure_levels: "Structure & Levels",
    indicator_interpretation: "Indicator Interpretation", confluence_reasoning: "Confluence Reasoning",
    risk_invalidation: "Risk & Invalidation", discipline: "Discipline",
  };
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="analyst-summary">
      <div className="tc-card p-8 text-center">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted">Analysis Scored · Process, Not Profit</div>
        <div className="font-heading text-5xl font-extrabold mt-2" data-testid="analyst-score">{s.score}<span className="text-tc-muted text-2xl">/100</span></div>
        <div className="flex justify-center gap-2 mt-3">
          {[1, 2, 3].map((i) => <Star key={i} className={`w-8 h-8 ${i <= s.stars ? "text-tc-xp fill-tc-xp" : "text-white/15"}`} />)}
        </div>
        {s.xp_awarded > 0 && <div className="font-mono text-tc-xp text-lg mt-2">+{s.xp_awarded} XP</div>}
        {s.mastered === false && <div className="text-tc-bear text-sm mt-2">Below mastery (70). Review the feedback and try again — every attempt sharpens your process.</div>}
      </div>

      <div className="tc-card p-5" data-testid="score-breakdown">
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-3">Scorecard</div>
        {Object.entries(s.breakdown).map(([k, v]) => (
          <div key={k} className="mb-2.5">
            <div className="flex justify-between text-[13px]"><span className="text-tc-sub">{labels[k] || k}</span><span className="font-mono">{v.score}/{v.max}</span></div>
            <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-full bg-tc-coach rounded-full" style={{ width: `${v.score / v.max * 100}%` }} /></div>
          </div>
        ))}
      </div>

      {s.stage_complete && <StageUnlocked copy={s.stage_unlocked_copy} />}
      <BossRetry practice={s.practice} />
      <UnlockList unlocks={s.new_unlocks} />

      {s.patterns?.length > 0 && (
        <div className="tc-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-2">What Craft Coach Noticed</div>
          {s.patterns.map((p, i) => (
            <div key={i} className="flex items-center gap-2 text-[13px] text-tc-sub mb-1">
              {["GOOD_PASS", "STRONG_RISK_DISCIPLINE"].includes(p.code) ? <Check className="w-3.5 h-3.5 text-tc-bull" /> : <ShieldAlert className="w-3.5 h-3.5 text-tc-xp" />}
              {p.code.replace(/_/g, " ").toLowerCase()}
            </div>
          ))}
        </div>
      )}

      {s.skill_deltas?.length > 0 && (
        <div className="tc-card p-5">
          <div className="text-[11px] font-mono uppercase tracking-widest text-tc-muted mb-3">Skills Improved</div>
          {s.skill_deltas.map((d) => (
            <div key={d.key} className="mb-2.5">
              <div className="flex justify-between text-[13px]"><span className="text-tc-sub">{d.label}</span><span className="font-mono text-tc-mastery">{(d.mastery * 100).toFixed(0)}% <span className="text-tc-bull">(+{d.delta}%)</span></span></div>
              <div className="h-1.5 rounded-full bg-white/10 mt-1 overflow-hidden"><div className="h-full bg-tc-mastery rounded-full" style={{ width: `${d.mastery * 100}%` }} /></div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button onClick={() => navigate("/training")} className="rounded-lg bg-white/5 border border-white/10 py-3 text-sm hover:bg-white/10">Trader Path</button>
        {s.next_mission ? (
          <button data-testid="next-mission-btn" onClick={() => navigate(`/training/${s.next_mission.id}`)}
            className="rounded-lg bg-tc-coach text-tc-bg font-semibold py-3 text-sm hover:bg-tc-coach/90 flex items-center justify-center gap-1.5">
            Next: {s.next_mission.name} <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button onClick={() => navigate("/dashboard")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold py-3 text-sm hover:bg-tc-coach/90">Continue Next Time</button>
        )}
      </div>
    </div>
  );
}
